<script setup>
const user = useState('user');
const municipality = useCookie('municipality');
const client = useSupabaseClient();
const { transitionColour } = useTailwindConfig();

const loading = ref(true);
const useTestData = ref(false);

// Data
const inquirySubmissions = ref([]);
const profiles = ref([]);
const topicOptions = ref([]);

// Filter states
const selectedTimeframe = ref('all-time');
const selectedPlanners = ref([]);
const selectedTopic = ref('All Topics');

// Filter options
const timeframeOptions = [
  { label: 'All-time', value: 'all-time' },
  { label: 'Last 30 days', value: '30-days' },
  { label: 'Last 3 months', value: '3-months' },
  { label: 'Last 6 months', value: '6-months' },
  { label: 'Last year', value: '1-year' }
];

const topicFilterOptions = ref(['All Topics']);

// Modal references
const totalInquiriesModalRef = ref();
const unresolvedInquiriesModalRef = ref();
const answeredInquiriesModalRef = ref();
const avgResponseTimeModalRef = ref();
const submissionMethodsModalRef = ref();
const topicsModalRef = ref();
const plannerAssignedModalRef = ref();
const plannerResolvedModalRef = ref();

// Filtered data
const filteredInquiries = computed(() => {
  let filtered = inquirySubmissions.value;

  // Apply timeframe filter
  if (selectedTimeframe.value !== 'all-time') {
    const now = new Date();
    let cutoffDate;

    switch (selectedTimeframe.value) {
      case '30-days':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3-months':
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '6-months':
        cutoffDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        break;
      case '1-year':
        cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
    }

    if (cutoffDate) {
      filtered = filtered.filter(inquiry => new Date(inquiry.dateSubmitted) >= cutoffDate);
    }
  }

  // Apply planner filter (multi-select, empty means all)
  if (Array.isArray(selectedPlanners.value) && selectedPlanners.value.length > 0) {
    filtered = filtered.filter(inquiry =>
      inquiry.assignees?.some(a => selectedPlanners.value.some(p => p.id === a.id))
    );
  }

  // Apply topic filter
  if (selectedTopic.value && selectedTopic.value !== 'All Topics') {
    filtered = filtered.filter(inquiry => inquiry.topic === selectedTopic.value);
  }

  return filtered;
});

// Analytics calculations
const analytics = ref(null);

// Define all possible submission methods for the pie chart
const allSubmissionMethods = [
  { key: 'assessment', name: 'Siteability Assessment', color: '#4a5a1b' },
  { key: 'email', name: 'Email', color: '#9d2700' },
  { key: 'phone', name: 'Phone', color: '#e3a057' },
  { key: 'in-person', name: 'In-Person', color: '#009d83' },
  { key: 'form', name: 'Form', color: '#565656' }
];

// Add computed for topic legend split
const maxTopicLegendItems = 5;
const sortedTopicData = computed(() =>
  (analytics.value?.topicData || []).slice().sort((a, b) => b.value - a.value)
);
const topicLegendMain = computed(() => sortedTopicData.value.slice(0, maxTopicLegendItems));
const topicLegendOverflow = computed(() => sortedTopicData.value.slice(maxTopicLegendItems));

// Submission method legend sort
const sortedSubmissionMethodData = computed(() =>
  (analytics.value?.submissionMethodData || []).slice().sort((a, b) => b.value - a.value)
);

// Planner stats calculation
const plannerStats = computed(() => {
  if (!profiles.value.length) return [];

  const stats = profiles.value.map((planner) => {
    const assignedInquiries = filteredInquiries.value.filter(inquiry =>
      inquiry.assignees && inquiry.assignees.some(assignee => assignee.id === planner.id)
    );

    const resolvedInquiries = assignedInquiries.filter(inquiry =>
      inquiry.status === 'complete'
    );

    // Calculate average response time for this planner's resolved inquiries
    let avgResponseTime = 0;
    if (resolvedInquiries.length > 0) {
      const totalHours = resolvedInquiries.reduce((sum, inquiry) => {
        if (inquiry.dateSubmitted && inquiry.dateMarkedAsComplete) {
          const submitted = new Date(inquiry.dateSubmitted);
          const completed = new Date(inquiry.dateMarkedAsComplete);
          const hours = Math.round((completed - submitted) / (1000 * 60 * 60));
          return sum + Math.max(0, hours);
        }
        return sum;
      }, 0);
      avgResponseTime = totalHours / resolvedInquiries.length;
    }

    return {
      planner,
      assignedCount: assignedInquiries.length,
      resolvedCount: resolvedInquiries.length,
      avgResponseTime: avgResponseTime > 0 ? formatSmartResponseTime(avgResponseTime) : 'N/A'
    };
  })
    .sort((a, b) => b.resolvedCount - a.resolvedCount) // Sort by resolved count desc
    .map((stat, index) => ({
      ...stat,
      rank: index + 1 // Assign rank based on position after sorting
    }));

  return stats;
});

// Selected planner for modals
const selectedPlanner = ref(null);

function openPlannerAssignedModal(planner) {
  selectedPlanner.value = planner;
  plannerAssignedModalRef.value?.open();
}

function openPlannerResolvedModal(planner) {
  selectedPlanner.value = planner;
  plannerResolvedModalRef.value?.open();
}

function fetchAnalytics() {
  const inquiries = filteredInquiries.value;

  // Basic metrics
  const totalInquiries = inquiries.length;
  const unresolvedInquiries = inquiries.filter(i => i.status !== 'complete').length;
  const answeredInquiries = inquiries.filter(i => i.status === 'complete').length;

  // Average response time
  const resolvedInquiries = inquiries.filter(i => i.status === 'complete' && i.dateSubmitted && i.dateMarkedAsComplete);
  let avgResponseTime = 0;
  if (resolvedInquiries.length > 0) {
    const totalHours = resolvedInquiries.reduce((sum, inquiry) => {
      const submitted = new Date(inquiry.dateSubmitted);
      const completed = new Date(inquiry.dateMarkedAsComplete);
      const hours = Math.round((completed - submitted) / (1000 * 60 * 60));
      return sum + Math.max(0, hours);
    }, 0);
    avgResponseTime = totalHours / resolvedInquiries.length;
  }

  // Most popular submission method
  const submissionMethods = {};
  inquiries.forEach(i => {
    if (i.submissionMethod) {
      submissionMethods[i.submissionMethod] = (submissionMethods[i.submissionMethod] || 0) + 1;
    }
  });
  const popularSubmissionMethod = Object.entries(submissionMethods)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

  // Most popular topic
  const topics = {};
  inquiries.forEach(i => {
    if (i.topic) {
      topics[i.topic] = (topics[i.topic] || 0) + 1;
    }
  });
  const popularTopic = Object.entries(topics)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

  // Monthly data for bar chart
  const monthlyData = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthInquiries = inquiries.filter(inquiry => {
      const submittedDate = new Date(inquiry.dateSubmitted);
      return submittedDate.getFullYear() === date.getFullYear() &&
        submittedDate.getMonth() === date.getMonth();
    });
    monthlyData.push({
      label: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      value: monthInquiries.length
    });
  }

  // Submission method data for pie chart
  const submissionMethodDataRaw = Object.entries(submissionMethods).map(([method, count]) => ({
    name: method === 'phone' ? 'Phone' :
      method === 'email' ? 'Email' :
        method === 'in-person' ? 'In-Person' :
          method === 'assessment' ? 'Siteability Assessment' :
            'Form',
    value: count,
    color: method === 'phone' ? '#e3a057' :
      method === 'email' ? '#9d2700' :
        method === 'in-person' ? '#009d83' :
          method === 'assessment' ? '#4a5a1b' :
            '#565656'
  }));

  // Always show all submission methods, even if value is 0
  const submissionMethodData = allSubmissionMethods.map(methodObj => {
    const found = submissionMethodDataRaw.find(d => d.name === methodObj.name);
    return {
      name: methodObj.name,
      value: found ? found.value : 0,
      color: methodObj.color
    };
  });

  // Topic data for pie chart - assign unique color to each topic
  const topicEntries = Object.entries(topics);
  const topicData = topicEntries.map(([topic, count], idx) => ({
    name: topic,
    value: count,
    color: `hsl(${(idx * 360) / topicEntries.length}, 70%, 55%)`
  }));

  return {
    totalInquiries,
    unresolvedInquiries,
    answeredInquiries,
    avgResponseTime: avgResponseTime > 0 ? formatSmartResponseTime(avgResponseTime) : 'N/A',
    popularSubmissionMethod: popularSubmissionMethod === 'phone' ? 'Phone' :
      popularSubmissionMethod === 'email' ? 'Email' :
        popularSubmissionMethod === 'in-person' ? 'In-Person' :
          popularSubmissionMethod === 'assessment' ? 'Siteability Assessment' :
            popularSubmissionMethod,
    popularTopic,
    monthlyData,
    submissionMethodData,
    topicData
  };
}

// Fetch data
async function fetchData() {
  try {
    if (useTestData.value) {
      // Generate random test data
      const testData = getRandomInquiryAnalyticsData(topicOptions.value);

      inquirySubmissions.value = testData.inquiries.map(inquiry => ({
        ...inquiry,
        assignees: inquiry.assignees.map(assigneeId =>
          testData.profiles.find(profile => profile.id === assigneeId)
        ).filter(Boolean)
      }));

      profiles.value = testData.profiles;

      // Add an avatar object to each profile
      const assigneeAvatarColors = getAssigneeAvatarColors(profiles.value);
      profiles.value.forEach(profile => {
        profile.avatar = {
          color: assigneeAvatarColors[profile.id],
          initials: profile.first_name.charAt(0).toUpperCase() + profile.last_name.charAt(0).toUpperCase()
        }
      });

      topicOptions.value = testData.topicOptions;
    }
    else {
      // Fetch real data
      const { data: inquiries, error: inquiriesError } = await client
        .schema(municipality.value)
        .from('inquiry_submissions')
        .select('id, subject, submitter_first_name, submitter_last_name, submitter_email, topic, status, assignees, submission_method, priority, date_submitted, date_last_updated, date_marked_as_complete, address, read, file_urls, email_status, is_demo')
        .order('date_last_updated', { ascending: false });

      if (inquiriesError) throw inquiriesError;

      // Filter inquiries to not include demo or draft
      inquirySubmissions.value = inquiries
        .filter(inquiry => inquiry.status !== 'draft' && !inquiry.is_demo)
        .map(inquiry => snakeToCamelCase(inquiry));

      // Fetch planners
      const { data: plannersData } = await client
        .from('profiles')
        .select('*')
        .eq('subscription', 'planner')
        .eq('municipality', municipality.value)
        .eq('is_demo', false);

      profiles.value = plannersData || [];

      // Sort so that the current user is first
      profiles.value.sort((a, b) => {
        if (a.id === user.value.id) return -1;
        if (b.id === user.value.id) return 1;
        return 0;
      });

      // Add an avatar object to each profile
      const assigneeAvatarColors = getAssigneeAvatarColors(profiles.value);
      profiles.value.forEach(profile => {
        profile.avatar = {
          color: assigneeAvatarColors[profile.id],
          initials: profile.first_name.charAt(0).toUpperCase() + profile.last_name.charAt(0).toUpperCase()
        }
      });

      // Populate assignees
      inquirySubmissions.value.forEach(inquiry => {
        if (inquiry.assignees) {
          inquiry.assignees = inquiry.assignees.map(assigneeId =>
            profiles.value.find(profile => profile.id === assigneeId)
          ).filter(Boolean);
        } else {
          inquiry.assignees = [];
        }
      });

      // Fetch topic options
      topicOptions.value = await fetchTopicOptions(municipality.value);
    }

    analytics.value = fetchAnalytics();

  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    loading.value = false;
  }
}

async function toggleTestData() {
  loading.value = true;
  useTestData.value = !useTestData.value;
  await fetchData();
}

function downloadCSV() {
  // Create CSV content
  const headers = ['Date Submitted', 'Address', 'Topic', 'Status', 'Submission Method', 'Assignees'];
  const rows = filteredInquiries.value.map(inquiry => [
    new Date(inquiry.dateSubmitted).toLocaleDateString(),
    inquiry.address || '',
    inquiry.topic || '',
    inquiry.status || '',
    inquiry.submissionMethod || '',
    inquiry.assignees.map(a => `${a.first_name} ${a.last_name}`).join('; ')
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  // Download file
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `inquiry-analytics-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function clearFilters() {
  selectedTimeframe.value = 'all-time';
  selectedPlanners.value = [];
  selectedTopic.value = 'All Topics';
}

// Watch filteredInquiries and recalculate analytics when it changes
watch(filteredInquiries, () => {
  analytics.value = fetchAnalytics();
}, { immediate: true });

onMounted(async () => {
  // Add 'All Topics' to the filter options
  const fetchedTopics = await fetchTopicOptions(municipality.value);
  topicFilterOptions.value = ['All Topics', ...fetchedTopics];
  await fetchData();

  useTrackEvent('view_inquiry_analytics');
});
</script>

<template>
  <div v-if="loading" class="flex justify-center items-center h-full grow">
    <LoadingSpinner class="w-14 h-14" />
  </div>
  <UContainer v-else class="min-h-screen max-w-6xl mx-auto py-16 px-16 space-y-8">
    <!-- Header -->
    <div class="flex justify-between items-center">
      <h1 class="text-4xl font-bold text-zinc-950 tracking-tight">Inquiry Analytics</h1>
      <UButton @click="toggleTestData" color="neutral" variant="outline" class="flex gap-1 items-center">
        <span>Use Test Data</span>
        <USwitch :model-value="useTestData" />
      </UButton>
    </div>

    <div class="flex gap-6 items-center">
      <!-- Filters -->
      <div class="grow grid grid-cols-3 gap-6">
        <!-- Timeframe Filter -->
        <USelectMenu v-model="selectedTimeframe" :items="timeframeOptions" label-key="label" value-key="value"
          class="flex-1 min-w-[180px]" placeholder="Timeframe">
          <template #default="{ modelValue }">
            <template v-if="modelValue">
              Timeframe: {{timeframeOptions.find(s => s.value === modelValue)?.label}}
            </template>
          </template>
        </USelectMenu>

        <!-- Planners Filter -->
        <CustomAvatarSelect v-model="selectedPlanners" :items="profiles" width="min-w-40" searchable
          placeholder="Planners" class="min-w-40" :class="transitionColour" />

        <!-- Topic Filter -->
        <USelectMenu v-model="selectedTopic" :items="topicFilterOptions" label-key="label" value-key="value"
          class="flex-1 min-w-[180px]" placeholder="Topic">
          <template #default="{ modelValue }">
            <template v-if="modelValue?.length">
              Topic: {{ modelValue }}
            </template>
          </template>
        </USelectMenu>
      </div>

      <!-- Clear Filters Button -->
      <UButton @click="clearFilters" color="neutral" variant="ghost"
        icon="material-symbols:filter-alt-off-outline-rounded" square />
    </div>

    <!-- Main Content Row: Cards and Bar Chart -->
    <div class="grid grid-cols-2 gap-8">
      <!-- Left Side: Metrics Cards -->
      <div class="grid grid-cols-3 gap-6 h-full">
        <!-- Row 1 -->
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="totalInquiriesModalRef.open()">
          <div class="text-xl font-bold text-primary-600 mb-1">{{ analytics.totalInquiries }}</div>
          <div class="text-sm text-custom-text-subtle">Total Inquiries</div>
        </div>
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="unresolvedInquiriesModalRef.open()">
          <div class="text-xl font-bold text-primary-600 mb-1">{{ analytics.unresolvedInquiries }}</div>
          <div class="text-sm text-custom-text-subtle">Unresolved Inquiries</div>
        </div>
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="answeredInquiriesModalRef.open()">
          <div class="text-xl font-bold text-primary-600 mb-1">{{ analytics.answeredInquiries }}</div>
          <div class="text-sm text-custom-text-subtle">Answered Inquiries</div>
        </div>
        <!-- Row 2 -->
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="avgResponseTimeModalRef.open()">
          <div class="text-xl font-bold text-primary-600 mb-1">{{ analytics.avgResponseTime }}</div>
          <div class="text-sm text-custom-text-subtle">Avg. Response Time</div>
        </div>
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="submissionMethodsModalRef.open()">
          <div class="font-bold text-primary-600 mb-1 break-words" :class="{
            'text-xl': analytics.popularSubmissionMethod.length <= 10,
            'text-lg': analytics.popularSubmissionMethod.length > 10 && analytics.popularSubmissionMethod.length <= 20,
            'text-sm': analytics.popularSubmissionMethod.length > 20,
          }">
            {{ analytics.popularSubmissionMethod }}
          </div>
          <div class="text-sm text-custom-text-subtle">Most Popular Submission Method</div>
        </div>
        <div
          class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 pt-6 flex flex-col items-start cursor-pointer hover:bg-neutral-100 transition-colors h-full"
          @click="topicsModalRef.open()">
          <div class="font-bold text-primary-600 mb-1 line-clamp-3" :class="{
            'text-xl': analytics.popularTopic.length <= 10,
            'text-lg': analytics.popularTopic.length > 10 && analytics.popularTopic.length <= 20,
            'text-sm': analytics.popularTopic.length > 20,
          }">
            {{ analytics.popularTopic }}
          </div>
          <div class="text-sm text-custom-text-subtle">Most Popular Inquiry Topic</div>
        </div>
      </div>

      <!-- Right Side: Bar Chart -->
      <div class="bg-neutral-50 border border-zinc-200 rounded-[4px] p-6 flex flex-col h-full grow">
        <div class="flex justify-between items-center">
          <h3 class="text-xl font-bold text-zinc-800">Inquiries</h3>
          <UButton variant="ghost" size="xs" @click="downloadCSV" icon="material-symbols:download-rounded">
            Download CSV
          </UButton>
        </div>
        <AnalyticsChart v-if="!loading" type="bar" :data="analytics.monthlyData"
          :colors="[getTailwindColorHex('--color-secondary-500')]" />
      </div>
    </div>

    <!-- Row 2 -->
    <div class="grid grid-cols-2 gap-8">
      <!-- Submission Methods Donut Chart -->
      <div class="bg-neutral-50 border border-zinc-200 rounded p-6 flex flex-col mb-auto">
        <h3 class="text-xl font-bold text-zinc-800 mb-4 leading-7">Inquiries by Submission Method</h3>
        <div class="flex grow items-center h-[300px]">
          <template v-if="!loading && analytics.submissionMethodData.some(d => d.value > 0)">
            <!-- Legend -->
            <div class="space-y-1 mr-4 mt-8">
              <div v-for="item in sortedSubmissionMethodData" :key="item.name" class="flex items-center gap-2">
                <div class="min-w-4 min-h-4 rounded" :style="{ backgroundColor: item.color }"></div>
                <span class="text-sm text-zinc-600">{{ item.name }} ({{ item.value }})</span>
              </div>
            </div>
            <!-- Chart or message -->
            <div class="flex-1 grow flex items-center justify-center h-[300px]">
              <AnalyticsChart type="donut" id="submission-method-chart" :data="analytics.submissionMethodData"
                :height="300" />
            </div>
          </template>
          <div v-else-if="!loading" class="text-zinc-400 text-center w-full">Not enough data to display a chart.</div>
        </div>
      </div>

      <!-- Topics Horizontal Bar Chart -->
      <div class="bg-neutral-50 border border-zinc-200 rounded p-6 flex flex-col">
        <h3 class="text-xl font-bold text-zinc-800 mb-4">Inquiries by Topic</h3>
        <div class="flex grow items-center h-full">
          <!-- Chart or message -->
          <div class="flex-1 grow flex items-center justify-center h-[300px] w-full flex-col">
            <template v-if="!loading && analytics.topicData.length > 0">
              <AnalyticsChart type="bar" id="topic-bar-chart" :data="analytics.topicData
                .slice(0, 10)
                .sort((a, b) => b.value - a.value)
                .map((item) => ({
                  label: item.name,
                  value: item.value,
                  color: getTailwindColorHex ? getTailwindColorHex('--color-secondary-500') : '#3B82F6'
                }))" :height="300" :horizontal="true" />
              <div v-if="analytics.topicData.length > 10" class="mt-2 flex justify-center w-full">
                <UPopover mode="click">
                  <button class="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700">
                    Show More
                    <UIcon name="i-lucide-chevron-down" size="16" />
                  </button>
                  <template #content>
                    <div class="space-y-1 p-2 min-w-[180px]">
                      <div v-for="item in analytics.topicData.slice(10).sort((a, b) => b.value - a.value)"
                        :key="item.name" class="flex items-center gap-2">
                        <div class="min-w-4 min-h-4 rounded"
                          :style="{ backgroundColor: getTailwindColorHex ? getTailwindColorHex('--color-secondary-500') : '#3B82F6' }">
                        </div>
                        <span class="text-sm text-zinc-600">{{ item.name }} ({{ item.value }})</span>
                      </div>
                    </div>
                  </template>
                </UPopover>
              </div>
            </template>
            <template v-else-if="!loading">
              <div class="text-zinc-400 text-center w-full">Not enough data to display a chart.</div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Planner Stats Section -->
    <div class="bg-neutral-50 border border-zinc-200 rounded p-6">
      <h3 class="text-xl font-bold text-zinc-800 mb-4">Planner Stats</h3>

      <div v-if="plannerStats.length > 0" class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-zinc-100">
              <th class="border border-zinc-200 px-4 py-3 text-center font-bold text-zinc-800"></th>
              <th class="border border-zinc-200 px-4 py-3 text-left font-bold text-zinc-800">Planner</th>
              <th class="border border-zinc-200 px-4 py-3 text-center font-bold text-zinc-800">Inquiries Assigned</th>
              <th class="border border-zinc-200 px-4 py-3 text-center font-bold text-zinc-800">Inquiries Resolved</th>
              <th class="border border-zinc-200 px-4 py-3 text-center font-bold text-zinc-800">Avg. Response Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stat in plannerStats" :key="stat.planner.id" class="bg-white">
              <td class="border border-zinc-200 px-4 py-3 text-center font-bold text-zinc-800">{{ stat.rank }}</td>
              <td class="border border-zinc-200 px-4 py-3 text-start text-zinc-800">
                {{ stat.planner.first_name }} {{ stat.planner.last_name }}
              </td>
              <td class="border border-zinc-200 px-4 py-3 text-center">
                <button @click="openPlannerAssignedModal(stat.planner)"
                  class="font-bold text-primary-500 hover:text-primary-400 underline cursor-pointer transition-colors">
                  {{ stat.assignedCount }}
                </button>
              </td>
              <td class="border border-zinc-200 px-4 py-3 text-center">
                <button @click="openPlannerResolvedModal(stat.planner)"
                  class="font-bold text-primary-500 hover:text-primary-400 underline cursor-pointer transition-colors">
                  {{ stat.resolvedCount }}
                </button>
              </td>
              <td class="border border-zinc-200 px-4 py-3 text-center text-zinc-800">{{ stat.avgResponseTime }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-zinc-400 text-center py-8">
        No planner data available.
      </div>
    </div>
  </UContainer>

  <!-- Total Inquiries Modal -->
  <LazyAnalyticsTotalInquiriesModal ref="totalInquiriesModalRef" :inquiries="filteredInquiries" />

  <!-- Unresolved Inquiries Modal -->
  <LazyAnalyticsUnresolvedInquiriesModal ref="unresolvedInquiriesModalRef" :inquiries="filteredInquiries" />

  <!-- Answered Inquiries Modal -->
  <LazyAnalyticsAnsweredInquiriesModal ref="answeredInquiriesModalRef" :inquiries="filteredInquiries" />

  <!-- Avg Response Time Modal -->
  <LazyAnalyticsAvgResponseTimeModal ref="avgResponseTimeModalRef" :inquiries="filteredInquiries" />

  <!-- Submission Methods Modal -->
  <LazyAnalyticsSubmissionMethodsModal ref="submissionMethodsModalRef" :inquiries="filteredInquiries" />

  <!-- Topics Modal -->
  <LazyAnalyticsTopicsModal ref="topicsModalRef" :inquiries="filteredInquiries" />

  <!-- Planner Assigned Inquiries Modal -->
  <LazyAnalyticsPlannerAssignedInquiriesModal ref="plannerAssignedModalRef" :inquiries="filteredInquiries"
    :planner="selectedPlanner" />

  <!-- Planner Resolved Inquiries Modal -->
  <LazyAnalyticsPlannerResolvedInquiriesModal ref="plannerResolvedModalRef" :inquiries="filteredInquiries"
    :planner="selectedPlanner" />
</template>